import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { payrollApi, staffApi } from '../services/api';
import {
  Plus,
  Calendar,
  DollarSign,
  Users,
  CheckCircle,
  AlertCircle,
  Play,
  CreditCard,
  Building2,
  ChevronRight,
  RefreshCw,
  X,
  Loader2,
  HelpCircle,
  Clock,
  TrendingUp,
  ArrowRight,
  Info,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import clsx from 'clsx';

type PayrollTab = 'periods' | 'run' | 'employees';

interface PayPeriod {
  id: string;
  startDate: string;
  endDate: string;
  payDate: string;
  status: string;
  totalGrossPayCents: number;
  totalNetPayCents: number;
  employeeCount: number;
  payrollRuns?: PayrollRun[];
}

interface PayrollRun {
  id: string;
  status: string;
  totalGrossPayCents: number;
  totalNetPayCents: number;
  totalTipsCents: number;
  totalOvertimeCents: number;
  employeeCount: number;
  items?: PayrollItem[];
  createdAt: string;
}

interface PayrollItem {
  id: string;
  employeeId: string;
  employee?: {
    firstName: string;
    lastName: string;
    hourlyRateCents?: number;
  };
  regularHours: string;
  overtimeHours: string;
  regularPayCents: number;
  overtimePayCents: number;
  tipsCents: number;
  grossPayCents: number;
  netPayCents: number;
  payoutStatus: string;
}

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
  hourlyRateCents: number;
  bankAccountId?: string;
  email?: string;
}

const defaultPeriodForm = {
  startDate: format(new Date(), 'yyyy-MM-dd'),
  endDate: format(addDays(new Date(), 14), 'yyyy-MM-dd'),
  payDate: format(addDays(new Date(), 19), 'yyyy-MM-dd'),
  notes: '',
};

export default function PayrollPage() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<PayrollTab>('periods');
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [showNewPeriodModal, setShowNewPeriodModal] = useState(false);
  const [periodForm, setPeriodForm] = useState(defaultPeriodForm);
  const [formError, setFormError] = useState('');

  // Fetch pay periods
  const { data: periodsData, isLoading: periodsLoading } = useQuery({
    queryKey: ['pay-periods'],
    queryFn: async () => {
      const response = await payrollApi.getPayPeriods();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch selected period details
  const { data: selectedPeriod, isLoading: periodLoading } = useQuery({
    queryKey: ['pay-period', selectedPeriodId],
    queryFn: async () => {
      if (!selectedPeriodId) return null;
      const response = await payrollApi.getPayPeriod(selectedPeriodId);
      return response.data?.data || response.data || null;
    },
    enabled: !!selectedPeriodId,
  });

  // Fetch staff for employee setup
  const { data: staffData, isLoading: staffLoading } = useQuery({
    queryKey: ['staff'],
    queryFn: async () => {
      const response = await staffApi.getAll();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Create pay period mutation
  const createPeriodMutation = useMutation({
    mutationFn: (data: typeof periodForm) => payrollApi.createPayPeriod({
      // Convert yyyy-MM-dd to ISO datetime string for backend validation
      startDate: new Date(data.startDate + 'T00:00:00').toISOString(),
      endDate: new Date(data.endDate + 'T23:59:59').toISOString(),
      payDate: new Date(data.payDate + 'T00:00:00').toISOString(),
      notes: data.notes || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-periods'] });
      setShowNewPeriodModal(false);
      setPeriodForm(defaultPeriodForm);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create pay period');
    },
  });

  // Create payroll run
  const createRunMutation = useMutation({
    mutationFn: (periodId: string) => payrollApi.createPayrollRun(periodId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-period', selectedPeriodId] });
      queryClient.invalidateQueries({ queryKey: ['pay-periods'] });
    },
  });

  // Approve payroll run
  const approveRunMutation = useMutation({
    mutationFn: (runId: string) => payrollApi.approvePayrollRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-period', selectedPeriodId] });
    },
  });

  // Process payroll run
  const processRunMutation = useMutation({
    mutationFn: (runId: string) => payrollApi.processPayrollRun(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pay-period', selectedPeriodId] });
    },
  });

  const formatCurrency = (cents: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(cents / 100);

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'open':
        return <span className="badge badge-info">Open</span>;
      case 'draft':
        return <span className="badge bg-slate-100 text-slate-600">Draft</span>;
      case 'pending_approval':
        return <span className="badge badge-warning">Pending Approval</span>;
      case 'approved':
        return <span className="badge badge-success">Approved</span>;
      case 'processing':
        return <span className="badge badge-info">Processing</span>;
      case 'completed':
        return <span className="badge badge-success">Completed</span>;
      case 'failed':
        return <span className="badge badge-danger">Failed</span>;
      default:
        return <span className="badge bg-slate-100 text-slate-600 capitalize">{status}</span>;
    }
  };

  // Use real data only - no mock data
  const periods = Array.isArray(periodsData) ? periodsData : [];
  const hasRealPeriods = periods.length > 0;

  // Only use real run data
  const realRun = selectedPeriod?.payrollRuns?.[0];
  const currentRun = realRun || null;

  const staff = Array.isArray(staffData) ? staffData : [];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll</h1>
          <p className="text-slate-500">Manage pay periods and process payroll</p>
        </div>
        <button
          onClick={() => setShowNewPeriodModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Pay Period
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'periods', label: 'Pay Periods', icon: Calendar },
          { id: 'run', label: 'Current Run', icon: DollarSign },
          { id: 'employees', label: 'Employee Setup', icon: Users },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id as PayrollTab)}
            className={clsx(
              'flex items-center gap-2 px-4 py-3 font-medium border-b-2 -mb-px transition-colors',
              activeTab === id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            )}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Pay Periods Tab */}
      {activeTab === 'periods' && (
        <div className="space-y-4">
          {periodsLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
              <p className="mt-4 text-slate-500">Loading pay periods...</p>
            </div>
          ) : (
            <>
              {periods.map((period: PayPeriod) => (
                <div
                  key={period.id}
                  onClick={() => {
                    setSelectedPeriodId(period.id);
                    setActiveTab('run');
                  }}
                  className="card p-4 cursor-pointer hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-primary-100 flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="font-semibold">
                          {formatDate(period.startDate)} - {formatDate(period.endDate)}
                        </p>
                        <p className="text-sm text-slate-500">
                          Pay Date: {formatDate(period.payDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Gross Pay</p>
                        <p className="font-semibold">
                          {formatCurrency(period.totalGrossPayCents || 0)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Employees</p>
                        <p className="font-semibold">{period.employeeCount || 0}</p>
                      </div>
                      {getStatusBadge(period.status)}
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                </div>
              ))}

              {periods.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No pay periods yet</p>
                  <p className="text-sm">Create your first pay period to get started</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Current Run Tab */}
      {activeTab === 'run' && (
        <div className="space-y-6">
          {periodLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
              <p className="mt-4 text-slate-500">Loading payroll data...</p>
            </div>
          ) : (
            <>
              {/* Payroll Workflow Guide */}
              {!currentRun && selectedPeriodId && (
                <div className="card p-4 bg-blue-50 border-blue-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-blue-900">How Payroll Works</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-sm text-blue-800">
                        <span className="flex items-center gap-1.5 bg-white/60 px-2 py-1 rounded">
                          <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                          Calculate Payroll
                        </span>
                        <ArrowRight className="w-4 h-4 text-blue-400" />
                        <span className="flex items-center gap-1.5 bg-white/60 px-2 py-1 rounded">
                          <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                          Review & Approve
                        </span>
                        <ArrowRight className="w-4 h-4 text-blue-400" />
                        <span className="flex items-center gap-1.5 bg-white/60 px-2 py-1 rounded">
                          <span className="w-5 h-5 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                          Process Payments
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 mt-2">
                        Click "Calculate Payroll" to pull hours from time entries and tips from closed orders.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Run Summary with Explanations */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="card p-4 group relative">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-slate-500">Gross Pay</p>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </div>
                  <p className="text-xl font-bold">
                    {formatCurrency(currentRun?.totalGrossPayCents || 0)}
                  </p>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    Total earnings before deductions
                  </div>
                </div>
                <div className="card p-4 group relative">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-slate-500">Net Pay</p>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </div>
                  <p className="text-xl font-bold text-primary-600">
                    {formatCurrency(currentRun?.totalNetPayCents || 0)}
                  </p>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
                    Take-home pay after taxes & deductions
                  </div>
                </div>
                <div className="card p-4 group relative">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-slate-500">Tips</p>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </div>
                  <p className="text-xl font-bold text-emerald-600">
                    {formatCurrency(currentRun?.totalTipsCents || 0)}
                  </p>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-48 z-10">
                    Credit card tips from POS. Cash tips tracked separately by staff.
                  </div>
                </div>
                <div className="card p-4 group relative">
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm text-slate-500">Overtime</p>
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                  </div>
                  <p className="text-xl font-bold text-amber-600">
                    {formatCurrency(currentRun?.totalOvertimeCents || 0)}
                  </p>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-slate-800 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-52 z-10">
                    Hours over 40/week paid at 1.5x hourly rate
                  </div>
                </div>
                <div className="card p-4">
                  <p className="text-sm text-slate-500">Employees</p>
                  <p className="text-xl font-bold">{currentRun?.employeeCount || 0}</p>
                </div>
              </div>

              {/* Calculation Details */}
              {currentRun && (
                <div className="card p-4 bg-slate-50 border-slate-200">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div className="text-sm text-slate-600">
                      <p className="font-medium text-slate-700 mb-1">How Pay is Calculated</p>
                      <ul className="space-y-1">
                        <li><strong>Regular Pay:</strong> Hours worked x hourly rate</li>
                        <li><strong>Overtime:</strong> Hours over 40/week x (hourly rate x 1.5)</li>
                        <li><strong>Tips:</strong> Credit card tips from closed orders (assigned by server)</li>
                        <li><strong>Deductions:</strong> Standard tax withholdings applied to get net pay</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  {currentRun && getStatusBadge(currentRun.status)}
                  {!currentRun && !selectedPeriodId && (
                    <span className="text-sm text-slate-500">Select a pay period to view payroll details</span>
                  )}
                </div>
                {currentRun?.status === 'draft' && (
                  <button
                    onClick={() => approveRunMutation.mutate(currentRun.id)}
                    disabled={approveRunMutation.isPending}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {approveRunMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Approve Payroll
                  </button>
                )}
                {currentRun?.status === 'approved' && (
                  <button
                    onClick={() => processRunMutation.mutate(currentRun.id)}
                    disabled={processRunMutation.isPending}
                    className="btn btn-primary flex items-center gap-2"
                  >
                    {processRunMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Process & Send Payments
                  </button>
                )}
                <button
                  onClick={() => {
                    const periodId = selectedPeriodId || (hasRealPeriods ? periods[0]?.id : null);
                    if (periodId) {
                      createRunMutation.mutate(periodId);
                    } else {
                      alert('Please create a pay period first before generating a payroll run.');
                    }
                  }}
                  disabled={createRunMutation.isPending || !hasRealPeriods}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className={clsx('w-4 h-4', createRunMutation.isPending && 'animate-spin')} />
                  {createRunMutation.isPending ? 'Calculating...' : 'Calculate Payroll'}
                </button>
              </div>

              {/* Employee Breakdown */}
              <div className="card overflow-hidden">
                <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold">Employee Breakdown</h2>
                    <p className="text-sm text-slate-500">Individual pay details for this period</p>
                  </div>
                  {currentRun?.items && currentRun.items.length > 0 && (
                    <div className="flex items-center gap-4 text-sm text-slate-600">
                      <div className="flex items-center gap-1.5">
                        <Clock className="w-4 h-4" />
                        <span>Hours from time clock</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-4 h-4" />
                        <span>Tips from POS orders</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="table-header">Employee</th>
                        <th className="table-header text-right">
                          <span className="flex items-center justify-end gap-1">
                            Regular
                            <span className="text-xs text-slate-400 font-normal">(hrs)</span>
                          </span>
                        </th>
                        <th className="table-header text-right">
                          <span className="flex items-center justify-end gap-1">
                            OT
                            <span className="text-xs text-slate-400 font-normal">(1.5x)</span>
                          </span>
                        </th>
                        <th className="table-header text-right">Regular Pay</th>
                        <th className="table-header text-right">OT Pay</th>
                        <th className="table-header text-right">Tips</th>
                        <th className="table-header text-right">
                          <span className="flex items-center justify-end gap-1">
                            Gross
                            <span className="text-xs text-slate-400 font-normal">(total)</span>
                          </span>
                        </th>
                        <th className="table-header text-right">
                          <span className="flex items-center justify-end gap-1 text-primary-700">
                            Net Pay
                            <span className="text-xs text-slate-400 font-normal">(take-home)</span>
                          </span>
                        </th>
                        <th className="table-header">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {currentRun?.items?.map((item: PayrollItem) => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="table-cell font-medium">
                            {item.employee?.firstName} {item.employee?.lastName}
                          </td>
                          <td className="table-cell text-right">{item.regularHours}</td>
                          <td className="table-cell text-right text-amber-600">
                            {item.overtimeHours !== '0.00' ? item.overtimeHours : '-'}
                          </td>
                          <td className="table-cell text-right">
                            {formatCurrency(item.regularPayCents || 0)}
                          </td>
                          <td className="table-cell text-right text-amber-600">
                            {(item.overtimePayCents || 0) > 0 ? formatCurrency(item.overtimePayCents) : '-'}
                          </td>
                          <td className="table-cell text-right text-emerald-600">
                            {(item.tipsCents || 0) > 0 ? formatCurrency(item.tipsCents) : '-'}
                          </td>
                          <td className="table-cell text-right font-medium">
                            {formatCurrency(item.grossPayCents || 0)}
                          </td>
                          <td className="table-cell text-right font-semibold text-primary-600">
                            {formatCurrency(item.netPayCents || 0)}
                          </td>
                          <td className="table-cell">
                            {item.payoutStatus === 'completed' ? (
                              <span className="badge badge-success">Paid</span>
                            ) : item.payoutStatus === 'processing' ? (
                              <span className="badge badge-info">Processing</span>
                            ) : item.payoutStatus === 'failed' ? (
                              <span className="badge badge-danger">Failed</span>
                            ) : (
                              <span className="badge bg-slate-100 text-slate-600">Pending</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(!currentRun?.items || currentRun.items.length === 0) && (
                  <div className="p-8 text-center">
                    <DollarSign className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-600 font-medium">No payroll data yet</p>
                    <p className="text-sm text-slate-500 mt-1">
                      Click "Calculate Payroll" to pull hours and tips for this pay period
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* Employee Setup Tab */}
      {activeTab === 'employees' && (
        <div className="space-y-4">
          <div className="card p-4 bg-primary-50 border-primary-200">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-primary-600 mt-0.5" />
              <div>
                <p className="font-medium text-primary-900">Direct Deposit Setup</p>
                <p className="text-sm text-primary-700 mt-1">
                  Employees need to connect their bank account to receive direct deposits.
                  Once connected, payments are processed automatically.
                </p>
              </div>
            </div>
          </div>

          {staffLoading ? (
            <div className="text-center py-12">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600" />
              <p className="mt-4 text-slate-500">Loading staff...</p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="table-header">Employee</th>
                    <th className="table-header">Role</th>
                    <th className="table-header">Hourly Rate</th>
                    <th className="table-header">Bank Account</th>
                    <th className="table-header">Payment Status</th>
                    <th className="table-header"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {staff.map((emp: StaffMember) => {
                    const hasBankAccount = !!emp.bankAccountId;
                    return (
                      <tr key={emp.id} className="hover:bg-slate-50">
                        <td className="table-cell font-medium">
                          {emp.firstName} {emp.lastName}
                        </td>
                        <td className="table-cell capitalize">{emp.primaryRole}</td>
                        <td className="table-cell">
                          {formatCurrency(emp.hourlyRateCents || 0)}/hr
                        </td>
                        <td className="table-cell">
                          {hasBankAccount ? (
                            <span className="flex items-center gap-2">
                              <CreditCard className="w-4 h-4 text-slate-400" />
                              Connected
                            </span>
                          ) : (
                            <span className="text-slate-400">Not connected</span>
                          )}
                        </td>
                        <td className="table-cell">
                          {hasBankAccount ? (
                            <span className="badge badge-success flex items-center gap-1 w-fit">
                              <CheckCircle className="w-3 h-3" />
                              Ready
                            </span>
                          ) : (
                            <span className="badge badge-warning flex items-center gap-1 w-fit">
                              <AlertCircle className="w-3 h-3" />
                              Setup Required
                            </span>
                          )}
                        </td>
                        <td className="table-cell">
                          {!hasBankAccount && (
                            <button
                              onClick={() => {
                                alert(`Bank account connection for ${emp.firstName} requires Plaid integration.\n\nTo enable direct deposit:\n1. Staff member provides bank routing/account numbers\n2. Admin verifies with micro-deposits\n\nContact support to enable Plaid for automated bank linking.`);
                              }}
                              className="btn btn-secondary btn-sm"
                            >
                              Connect Bank
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {staff.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No staff members found. Add staff members first.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* New Pay Period Modal */}
      {showNewPeriodModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">New Pay Period</h2>
              <button
                onClick={() => {
                  setShowNewPeriodModal(false);
                  setPeriodForm(defaultPeriodForm);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={(e) => {
              e.preventDefault();
              createPeriodMutation.mutate(periodForm);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    className="input w-full"
                    value={periodForm.startDate}
                    onChange={(e) => setPeriodForm({ ...periodForm, startDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    className="input w-full"
                    value={periodForm.endDate}
                    onChange={(e) => setPeriodForm({ ...periodForm, endDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Pay Date *
                  </label>
                  <input
                    type="date"
                    className="input w-full"
                    value={periodForm.payDate}
                    onChange={(e) => setPeriodForm({ ...periodForm, payDate: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    className="input w-full"
                    rows={2}
                    value={periodForm.notes}
                    onChange={(e) => setPeriodForm({ ...periodForm, notes: e.target.value })}
                    placeholder="Optional notes..."
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewPeriodModal(false);
                    setPeriodForm(defaultPeriodForm);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createPeriodMutation.isPending}
                >
                  {createPeriodMutation.isPending ? 'Creating...' : 'Create Pay Period'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
